/**
 * Turns raw Street View panoramas found around an employer address into a short
 * list of site viewpoints (nearest camera, opposite-side camera, side approaches).
 * Roles are distance/bearing heuristics — not door or entrance detection.
 */

export type LatLngPoint = { lat: number; lng: number };

export type CompassPoint = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

export type SiteViewpointRole = 'ENTRANCE' | 'EXIT' | 'SIDE';

export type SiteViewpointCandidate = {
  panoId: string;
  location: LatLngPoint;
  /** Google's own description of the pano location, when present. */
  description?: string | null;
  /** Probe ring that discovered this candidate (adaptive expansion). */
  ringOffsetM?: number;
  /** Street View capture month/year when Google returns it (e.g. "2023-05"). */
  imageDate?: string | null;
};

export type SiteViewpoint = {
  panoId: string;
  location: LatLngPoint;
  role: SiteViewpointRole;
  /** 1-based badge shown on the map marker and in the rail. */
  sequence: number;
  title: string;
  purpose: string;
  /** Camera bearing that points from the pano back at the building. */
  heading: number;
  pitch: number;
  distanceMeters: number;
  /** Which face of the building this camera is standing on. */
  side: CompassPoint;
  description: string | null;
  /** True when accepted from an outer adaptive ring (may be a neighbouring block). */
  distantCoverage: boolean;
  /** Street View capture date string from metadata, when available. */
  imageDate: string | null;
};

const EARTH_RADIUS_M = 6_371_000;

/** Probe rings: eight faces so a corner lot still yields an opposite-side camera. */
export const SITE_PROBE_BEARINGS = [0, 45, 90, 135, 180, 225, 270, 315] as const;

/**
 * Adaptive Street View search rings (offset from building → road, then radius).
 * Dense urban sites usually resolve on the first ring; campuses expand outward.
 */
export const SITE_PROBE_RINGS = [
  { offsetM: 40, radiusM: 55 },
  { offsetM: 80, radiusM: 90 },
  { offsetM: 160, radiusM: 140 },
] as const;

/** Stop expanding once we have this many distinct panoramas. */
export const SITE_PROBE_TARGET_COUNT = 3;

/**
 * Site ring drawn on aerial + max distance for labelling viewpoints.
 * Kept in sync with empty-state copy (was previously 130 m vs 150 m ring).
 */
export const SITE_COVERAGE_RADIUS_M = 150;

/** Outer adaptive ring may accept cameras beyond the site ring; hard cap. */
export const SITE_MAX_DISTANCE_M = 500;

/** Warn when a viewpoint is farther than this (neighbouring block risk). */
export const SITE_DISTANT_WARNING_M = 150;

const COMPASS_POINTS: CompassPoint[] = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

const COMPASS_NAMES: Record<CompassPoint, string> = {
  N: 'north',
  NE: 'north-east',
  E: 'east',
  SE: 'south-east',
  S: 'south',
  SW: 'south-west',
  W: 'west',
  NW: 'north-west',
};

const toRad = (deg: number): number => (deg * Math.PI) / 180;
const toDeg = (rad: number): number => (rad * 180) / Math.PI;

export function distanceMeters(a: LatLngPoint, b: LatLngPoint): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function bearingBetween(from: LatLngPoint, to: LatLngPoint): number {
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

export function offsetPoint(
  origin: LatLngPoint,
  bearingDegrees: number,
  meters: number,
): LatLngPoint {
  const angular = meters / EARTH_RADIUS_M;
  const bearing = toRad(bearingDegrees);
  const lat1 = toRad(origin.lat);
  const lng1 = toRad(origin.lng);

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(bearing),
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angular) * Math.cos(lat1),
      Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2),
    );

  return { lat: toDeg(lat2), lng: ((toDeg(lng2) + 540) % 360) - 180 };
}

export function compassPoint(degrees: number): CompassPoint {
  const normalized = ((degrees % 360) + 360) % 360;
  return COMPASS_POINTS[Math.round(normalized / 45) % 8]!;
}

export function compassName(point: CompassPoint): string {
  return COMPASS_NAMES[point];
}

/** Smallest angle between two bearings, 0–180. */
export function angleDelta(a: number, b: number): number {
  const raw = Math.abs(((a - b) % 360) + 360) % 360;
  return raw > 180 ? 360 - raw : raw;
}

export function formatMeters(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

/**
 * Presentation copy only — roles stay ENTRANCE/EXIT/SIDE internally.
 * Wording describes nearest / opposite / side cameras, not doors or exits.
 */
const ROLE_PURPOSE: Record<SiteViewpointRole, string> = {
  ENTRANCE:
    'Nearest street-level camera to the address — useful for signage and street frontage.',
  EXIT: 'Camera on the opposite side of the building (by bearing) — useful for the far frontage.',
  SIDE: 'Side approach camera — useful for parking, neighbouring units and access roads.',
};

const DEFAULT_MAX_VIEWPOINTS = 6;
/** Opposite-ish faces only: avoids labelling the next camera down the same street as rear. */
const EXIT_MIN_ANGLE = 110;

export function labelSiteViewpoints(
  building: LatLngPoint,
  candidates: SiteViewpointCandidate[],
  options?: { maxDistanceMeters?: number; maxViewpoints?: number; distantWarningMeters?: number },
): SiteViewpoint[] {
  const maxDistance = options?.maxDistanceMeters ?? SITE_MAX_DISTANCE_M;
  const maxViewpoints = options?.maxViewpoints ?? DEFAULT_MAX_VIEWPOINTS;
  const distantWarning = options?.distantWarningMeters ?? SITE_DISTANT_WARNING_M;

  const nearestByPano = new Map<string, { candidate: SiteViewpointCandidate; distance: number }>();
  for (const candidate of candidates) {
    if (!candidate?.panoId) continue;
    const { lat, lng } = candidate.location ?? {};
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;

    const distance = distanceMeters(building, candidate.location);
    if (distance > maxDistance) continue;

    const existing = nearestByPano.get(candidate.panoId);
    if (!existing || distance < existing.distance) {
      nearestByPano.set(candidate.panoId, { candidate, distance });
    }
  }

  const ordered = [...nearestByPano.values()]
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxViewpoints);
  if (ordered.length === 0) return [];

  const sides = ordered.map((entry) => bearingBetween(building, entry.candidate.location));
  const frontSide = sides[0]!;

  let exitIndex = -1;
  let widestDelta = EXIT_MIN_ANGLE;
  for (let i = 1; i < ordered.length; i += 1) {
    const delta = angleDelta(sides[i]!, frontSide);
    if (delta >= widestDelta) {
      widestDelta = delta;
      exitIndex = i;
    }
  }

  return ordered.map((entry, index) => {
    const side = compassPoint(sides[index]!);
    const role: SiteViewpointRole =
      index === 0 ? 'ENTRANCE' : index === exitIndex ? 'EXIT' : 'SIDE';
    const distantCoverage = entry.distance > distantWarning;
    const title =
      role === 'ENTRANCE'
        ? 'Nearest street camera'
        : role === 'EXIT'
          ? `Opposite side (${compassName(side)})`
          : `${compassName(side)} side approach`;

    const purpose = distantCoverage
      ? `${ROLE_PURPOSE[role]} This camera is ${formatMeters(entry.distance)} away and may be a neighbouring block.`
      : ROLE_PURPOSE[role];

    return {
      panoId: entry.candidate.panoId,
      location: entry.candidate.location,
      role,
      sequence: index + 1,
      title: title.charAt(0).toUpperCase() + title.slice(1),
      purpose,
      heading: Math.round(bearingBetween(entry.candidate.location, building)),
      pitch: role === 'ENTRANCE' ? 4 : 2,
      distanceMeters: entry.distance,
      side,
      description: entry.candidate.description?.trim() || null,
      distantCoverage,
      imageDate: entry.candidate.imageDate?.trim() || null,
    } satisfies SiteViewpoint;
  });
}

/**
 * Pure adaptive-ring planner: given distinct pano counts found per ring in order,
 * returns how many rings should have been probed (stop early once target met).
 */
export function ringsNeededForCoverage(
  distinctCountsAfterEachRing: number[],
  targetCount = SITE_PROBE_TARGET_COUNT,
): number {
  if (distinctCountsAfterEachRing.length === 0) return 0;
  for (let i = 0; i < distinctCountsAfterEachRing.length; i += 1) {
    if ((distinctCountsAfterEachRing[i] ?? 0) >= targetCount) return i + 1;
  }
  return distinctCountsAfterEachRing.length;
}
